import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { AppTitleLogEventsResponse, AppTitleLogGroupBy } from '@/types/reports';
import { ACTIVITY_LOG_EVENTS_RESPONSE } from '@/data/appTitleLogEvents';

export interface UseAppTitleLogEventsArgs {
  /** Period query string in the same shape ReportsMonthlyPage builds — e.g.
   *  `month=2026-04` or `period=week&week=2026-W17`. Pass `null` to disable. */
  periodQs: string | null;
  /** Required identity of the row being drilled into. Both can be empty
   *  strings — the list endpoint collapses null app/title to ''. */
  app: string;
  title: string;
  clientId?: string;
  projectId?: string;
  employeeId?: string;
  groupBy?: AppTitleLogGroupBy;
  groupKey?: string;
  limit?: number;
}

interface UseAppTitleLogEventsResult {
  data: AppTitleLogEventsResponse | null;
  isLoading: boolean;
}

/**
 * App & Window Log event-list data source — task 169 drill-down.
 *
 * Fires the JSON endpoint with the row identity (`app` + `title`), the
 * encoded period, the optional narrowing filters, and the optional
 * `groupBy` + `groupKey` when the source row was grouped. In mockup mode
 * returns the task-177 fixture so the new screenshot thumbnail column
 * has a deterministic surface to render against without contacting any
 * backend.
 */
export function useAppTitleLogEvents(args: UseAppTitleLogEventsArgs): UseAppTitleLogEventsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  // The endpoint requires `app` and `title`; accept empty-string but not
  // `undefined`, which would mean the caller hasn't resolved them yet.
  const enabled =
    !mockupMode && !!orgId && !!args.periodQs && args.app !== undefined && args.title !== undefined;

  const query = useQuery({
    queryKey: ['appTitleLogEvents', orgId, args],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams(args.periodQs ?? '');
      params.set('app', args.app);
      params.set('title', args.title);
      if (args.clientId) params.set('clientId', args.clientId);
      if (args.projectId) params.set('projectId', args.projectId);
      if (args.employeeId) params.set('employeeId', args.employeeId);
      if (args.groupBy) params.set('groupBy', args.groupBy);
      if (args.groupKey) params.set('groupKey', args.groupKey);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      return apiFetch<AppTitleLogEventsResponse>(
        `/api/v1/orgs/${orgId}/reports/activity-log/events?${params.toString()}`,
        token,
      );
    },
    enabled,
  });

  if (mockupMode) return { data: ACTIVITY_LOG_EVENTS_RESPONSE, isLoading: false };
  return { data: query.data ?? null, isLoading: query.isLoading };
}
